import { Variants } from '@sky-distribution/digital-gp-submission';
import {
  createContext, FC, useCallback, useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CoveragesDetails,
  CoverageWithMeta,
} from '../Coverage.type';
import {
  filterSelectedCoveragesByName as filterSelectedCoveragesByNameUtil,
  generateCoveragesWithMeta,
  getTermPrice,
  getTotalPrice,
  mapToLegacyVariants,
} from '../coverageUtils';
import { updateVariants as updateVariantsUtil } from './coverages/updateVariants';
import { GenericSubmissionContextType, SubmissionProviderProps } from './GenericSubmission.type';

export const GenericSubmissionContext = createContext<GenericSubmissionContextType>(
  {} as GenericSubmissionContextType,
);

export const useGenericSubmission = (): GenericSubmissionContextType => useContext(GenericSubmissionContext);

export const GenericSubmissionProvider: FC<SubmissionProviderProps> = ({
  children,
  configuration,
}) => {
  const { t } = useTranslation();
  const {
    submission,
    digitalSubmission,
    digitalCoverages,
    coveragesMeta,
    submissionOptions,
    coveragesDetails,
  } = configuration;

  const selectedVariantId = 0;
  // Legacy PC submission.variants may be a non-array stub; only real arrays are usable here.
  const legacyVariants = Array.isArray(submission?.variants) ? submission.variants : undefined;
  const variants = legacyVariants
    ?? mapToLegacyVariants(digitalSubmission?.variants as Variants)
    ?? [];

  const selectedDigitalSubmissionVariant = Array.isArray(digitalSubmission?.variants)
    ? digitalSubmission.variants[selectedVariantId]
    : undefined;

  const coveragesWithMeta = generateCoveragesWithMeta(
    variants,
    digitalCoverages ?? {},
    coveragesMeta ?? {},
    selectedVariantId,
    submissionOptions ?? { propertyKey: 'riskObject', propertyCoveragesKey: 'coverages' },
    coveragesDetails,
  );

  const getPrice = useCallback(
    () => getTotalPrice(coveragesWithMeta, 'pln', selectedDigitalSubmissionVariant),
    [coveragesWithMeta, selectedDigitalSubmissionVariant],
  );

  const updateVariants = useCallback(
    (coverages: CoverageWithMeta[]) => updateVariantsUtil(
      variants[selectedVariantId],
      coverages,
      submission,
      submissionOptions,
    ), [submissionOptions, submission, variants],
  );

  const filterSelectedCoveragesByName = useCallback(
    (modalDetails: CoveragesDetails) => filterSelectedCoveragesByNameUtil(coveragesWithMeta, modalDetails),
    [coveragesWithMeta],
  );

  const getTermPriceByCodeIdentifier = useCallback(
    (
      code: string,
      showCurrency?: boolean,
    ) => getTermPrice(code, coveragesWithMeta, t, showCurrency), [coveragesWithMeta, t],
  );

  return (
    <GenericSubmissionContext.Provider value={{
      coveragesWithMeta,
      getPrice,
      updateVariants,
      filterSelectedCoveragesByName,
      getTermPriceByCodeIdentifier,
      onUpdateCoverage: configuration.onUpdateCoverage,
    }}>
      {children}
    </GenericSubmissionContext.Provider>
  );
};
